import { useEffect, useRef } from "react";
import { useModelStore } from "../store/useModelStore";

interface ShortcutHandlers {
  onDelete?: () => void;
  onEscape?: () => void;
  onUndo?: () => void;
  onRedo?: () => void;
  onCopy?: () => void;
  onPaste?: () => void;
  onSelectAll?: () => void;
}

/**
 * Global keyboard shortcuts hook
 * Handles common keyboard shortcuts across the application
 * 
 * Supported shortcuts:
 * - Delete/Backspace: Delete selected elements
 * - Escape: Clear selection and exit modes
 * - Ctrl+Z: Undo
 * - Ctrl+Shift+Z / Ctrl+Y: Redo
 * - Ctrl+C: Copy
 * - Ctrl+V: Paste
 * - Ctrl+A: Select all nodes and beams
 * - Space: Prevented from scrolling
 */
export const useKeyboardShortcuts = (handlers: ShortcutHandlers = {}) => {
  const modelStore = useModelStore();
  const {
    nodes,
    beams,
    selectedNodeIds,
    selectedBeamIds,
    deleteNode,
    deleteBeam,
    clearSelection,
    selectMultipleNodes,
    selectMultipleBeams,
  } = modelStore;

  // Keep track of modifier key states
  const modifierKeys = useRef({
    ctrlKey: false,
    metaKey: false,
    shiftKey: false,
  });

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't trigger shortcuts if user is typing in an input
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement
      ) {
        return;
      }

      // Update modifier key states
      modifierKeys.current = {
        ctrlKey: event.ctrlKey,
        metaKey: event.metaKey,
        shiftKey: event.shiftKey,
      };

      switch (event.key) {
        case "Delete":
        case "Backspace":
          if (handlers.onDelete) {
            handlers.onDelete();
          } else {
            // Default delete behavior
            selectedNodeIds.forEach((nodeId) => deleteNode(nodeId));
            selectedBeamIds.forEach((beamId) => deleteBeam(beamId));
          }
          break;

        case "Escape":
          if (handlers.onEscape) {
            handlers.onEscape();
          } else {
            // Default escape behavior - clear all selections
            clearSelection();
          }
          break;

        case "z":
          if (modifierKeys.current.ctrlKey || modifierKeys.current.metaKey) {
            if (modifierKeys.current.shiftKey) {
              // Ctrl/Cmd + Shift + Z = Redo
              if (handlers.onRedo) {
                event.preventDefault();
                handlers.onRedo();
              }
            } else {
              // Ctrl/Cmd + Z = Undo
              if (handlers.onUndo) {
                event.preventDefault();
                handlers.onUndo();
              }
            }
          }
          break;

        case "y":
          if (
            (modifierKeys.current.ctrlKey || modifierKeys.current.metaKey) &&
            handlers.onRedo
          ) {
            // Ctrl/Cmd + Y = Redo (alternative)
            event.preventDefault();
            handlers.onRedo();
          }
          break;

        case "c":
          if (modifierKeys.current.ctrlKey || modifierKeys.current.metaKey) {
            // Ctrl/Cmd + C = Copy
            if (handlers.onCopy) {
              event.preventDefault();
              handlers.onCopy();
            }
          }
          break;

        case "v":
          if (modifierKeys.current.ctrlKey || modifierKeys.current.metaKey) {
            // Ctrl/Cmd + V = Paste
            if (handlers.onPaste) {
              event.preventDefault();
              handlers.onPaste();
            }
          }
          break;

        case "a":
          if (modifierKeys.current.ctrlKey || modifierKeys.current.metaKey) {
            // Ctrl/Cmd + A = Select All
            event.preventDefault();
            
            if (handlers.onSelectAll) {
              handlers.onSelectAll();
            } else {
              // Default select all behavior - select all nodes and beams
              const allNodeIds = nodes.map((n) => n.id);
              const allBeamIds = beams.map((b) => b.id);
              
              selectMultipleNodes(allNodeIds);
              selectMultipleBeams(allBeamIds);
            }
          }
          break;

        case " ":
          // Prevent space from scrolling the page
          event.preventDefault();
          break;
      }
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      // Update modifier key states on key up
      modifierKeys.current = {
        ctrlKey: event.ctrlKey,
        metaKey: event.metaKey,
        shiftKey: event.shiftKey,
      };
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [
    handlers,
    nodes,
    beams,
    selectedNodeIds,
    selectedBeamIds,
    deleteNode,
    deleteBeam,
    clearSelection,
    selectMultipleNodes,
    selectMultipleBeams,
  ]);
};

export default useKeyboardShortcuts;
